import qs from "qs";

import config from "../../config";
import isAllowedMarketingParam, {
  LAST_CAPTURED_AT_KEY
} from "./isAllowedMarketingParam";

function getStorageKey() {
  return config.marketingParams?.storageKey || "marketingParams";
}

function canUseLocalStorage() {
  return (
    typeof window !== "undefined" && typeof window.localStorage !== "undefined"
  );
}

function createNoopBackend() {
  return {
    getItem() {
      return null;
    },
    setItem() {},
    removeItem() {}
  };
}

function createLocalStorageBackend() {
  if (!canUseLocalStorage()) {
    return createNoopBackend();
  }

  return {
    getItem(key) {
      return window.localStorage.getItem(key);
    },
    setItem(key, value) {
      window.localStorage.setItem(key, value);
    },
    removeItem(key) {
      window.localStorage.removeItem(key);
    }
  };
}

let storageBackend = createLocalStorageBackend();

export function setStorageBackend(nextBackend) {
  if (
    nextBackend &&
    typeof nextBackend.getItem === "function" &&
    typeof nextBackend.setItem === "function" &&
    typeof nextBackend.removeItem === "function"
  ) {
    storageBackend = nextBackend;
    return;
  }

  storageBackend = createLocalStorageBackend();
}

// TODO: Add native AsyncStorage backend wiring when marketing params are needed on mobile.

function normalizeParamValue(rawValue) {
  if (typeof rawValue !== "string") {
    return null;
  }

  const trimmedValue = rawValue.trim();

  return trimmedValue || null;
}

export function read() {
  try {
    const raw = storageBackend.getItem(getStorageKey());

    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw);

    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
}

export function write(data) {
  storageBackend.setItem(getStorageKey(), JSON.stringify(data));
}

export function clear() {
  storageBackend.removeItem(getStorageKey());
}

export function captureFromSearch(search) {
  const searchParams = qs.parse(search, { ignoreQueryPrefix: true });
  const stored = { ...read() };
  const capturedKeys = [];
  const capturedAt = Date.now();

  Object.entries(searchParams).forEach(([name, rawValue]) => {
    if (!isAllowedMarketingParam(name)) {
      return;
    }

    const value = normalizeParamValue(rawValue);

    if (!value) {
      return;
    }

    stored[name] = { value, capturedAt };
    capturedKeys.push(name);
  });

  if (capturedKeys.length === 0) {
    return { changed: false, capturedKeys: [] };
  }

  stored[LAST_CAPTURED_AT_KEY] = capturedAt;
  write(stored);

  return { changed: true, capturedKeys };
}

export function stripCapturedParamsFromSearch(search, capturedKeys) {
  if (!capturedKeys.length) {
    return search || "";
  }

  const searchParams = qs.parse(search, { ignoreQueryPrefix: true });

  capturedKeys.forEach(name => {
    delete searchParams[name];
  });

  const nextSearch = qs.stringify(searchParams, { addQueryPrefix: true });

  return nextSearch || "";
}

export function getAll() {
  const stored = read();
  const params = { ...stored };

  delete params[LAST_CAPTURED_AT_KEY];

  return params;
}

export function getRaw() {
  return read();
}

const marketingParamsStorage = {
  read,
  write,
  clear,
  captureFromSearch,
  stripCapturedParamsFromSearch,
  getAll,
  getRaw,
  setStorageBackend
};

export default marketingParamsStorage;
